import { useState, useEffect, useCallback } from 'react';
import { 
  getJobs, 
  getJob, 
  deleteJob,
  downloadJobResults,
  pollJobStatus,
  extractOrganizationId,
  type SimilarityJob
} from '../api/similarity';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './useToast';
import { dispatchTokenExpiredEvent } from '../utils/authEvents';

import { logger } from '../utils/logger';

export const useSimilarityJobs = (projectId?: string) => {
  const [jobs, setJobs] = useState<SimilarityJob[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const toast = useToast();

  const isAuthenticated = !!user;

  // Handle token expiration by logging out the user
  const handleTokenExpired = () => {
    dispatchTokenExpiredEvent();
  };

  // Load jobs on mount and when user or projectId changes
  useEffect(() => {
    if (isAuthenticated && user && projectId !== undefined) {
      loadJobs();
    } else if (!isAuthenticated || !user) {
      setJobs([]);
      setError(null);
    }
    // Don't load jobs if projectId is undefined - this means we're not in a view that needs jobs
  }, [isAuthenticated, user, projectId]);

  const loadJobs = async () => {
    if (!isAuthenticated || !user) return;
    
    // Don't load jobs if no projectId is provided
    if (projectId === undefined) {
      setJobs([]);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Check if user has an organization
      const organizationId = extractOrganizationId(user);
      if (!organizationId) {
        throw new Error('No organization found for your account. Please contact support.');
      }
      
      const jobsData = await getJobs(projectId, user, handleTokenExpired);
      setJobs(jobsData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load jobs';
      setError(errorMessage);
      toast.error('Failed to load jobs', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadJob = async (jobId: string) => {
    if (!isAuthenticated || !user) {
      toast.error('Authentication required', 'Please log in to download job results');
      return;
    }

    // Find the job to get its download_url
    const job = jobs.find(j => j.id === jobId);
    if (!job) {
      toast.error('Job not found', 'Unable to locate the job for download');
      return;
    }

    try {
      toast.success('Starting download...', 'Preparing your similarity results');
      
      let blob: Blob;
      
      // Try the API download first
      try {
        blob = await downloadJobResults(jobId, user, handleTokenExpired);
      } catch (apiError) {
        logger.warn('API download failed, trying direct URL:', apiError);
        
        // Fallback to direct download URL if available
        if (job.download_url) {
          try {
            const response = await fetch(job.download_url, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
              }
            });
            
            if (!response.ok) {
              throw new Error(`Direct download failed: ${response.status}`);
            }
            
            blob = await response.blob();
          } catch (directError) {
            logger.error('Direct download also failed:', directError);
            throw apiError; // Re-throw the original API error
          }
        } else {
          throw apiError; // Re-throw the original API error
        }
      }
      
      // Check if blob is empty or invalid
      if (!blob || blob.size === 0) {
        toast.error('Download failed', 'The results file appears to be empty or invalid');
        return;
      }
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `similarity-results-${jobId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Download completed', 'Your similarity results have been downloaded successfully');
    } catch (err) {
      logger.error('Download error:', err);
      
      let errorMessage = 'Failed to download job results';
      if (err instanceof Error) {
        errorMessage = err.message;
        
        // Provide more specific error messages
        if (errorMessage.includes('502')) {
          errorMessage = 'Download service is temporarily unavailable. Please try again in a few minutes.';
        } else if (errorMessage.includes('404')) {
          errorMessage = 'Results file not found. The job may not have completed successfully.';
        } else if (errorMessage.includes('Authentication')) {
          errorMessage = 'Your session has expired. Please log in again.';
        }
      }
      
      toast.error('Download failed', errorMessage);
    }
  };

  const startPollingJob = useCallback((jobId: string) => {
    if (!isAuthenticated || !user) return;
    
    const handleUpdate = (updatedJob: SimilarityJob) => {
      setJobs(prevJobs => {
        const existingJobIndex = prevJobs.findIndex(job => job.id === jobId);
        if (existingJobIndex >= 0) {
          // Update existing job
          return prevJobs.map(job => 
            job.id === jobId ? updatedJob : job
          );
        } else {
          // Add new job if it doesn't exist
          return [...prevJobs, updatedJob];
        }
      });
      
      // Show completion notification
      if (updatedJob.status === 'completed') {
        const itemsText = updatedJob.total_items ? ` (${updatedJob.total_items} items processed)` : '';
        toast.success('Job completed', `Job ${jobId} has finished processing${itemsText}`);
      } else if (updatedJob.status === 'failed') {
        toast.error('Job failed', `Job ${jobId} failed: ${updatedJob.error_message || 'Unknown error'}`);
      }
    };
    
    pollJobStatus(jobId, user, handleTokenExpired, handleUpdate);
  }, [isAuthenticated, user, toast, handleTokenExpired]);

  const addJob = useCallback((job: SimilarityJob) => {
    setJobs(prevJobs => {
      const existingJobIndex = prevJobs.findIndex(j => j.id === job.id);
      if (existingJobIndex >= 0) {
        // Update existing job
        return prevJobs.map(j => j.id === job.id ? job : j);
      } else {
        // Add new job
        return [job, ...prevJobs];
      }
    });
  }, []);

  const refreshJobs = () => {
    loadJobs();
  };

  const getJobById = async (jobId: string) => {
    if (!isAuthenticated || !user) {
      toast.error('Authentication required', 'Please log in to view job details');
      return null;
    }

    try {
      const job = await getJob(jobId, user, handleTokenExpired);
      return job;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load job details';
      toast.error('Failed to load job', errorMessage);
      return null;
    }
  };

  const deleteJobById = async (jobId: string) => {
    if (!isAuthenticated || !user) {
      toast.error('Authentication required', 'Please log in to delete jobs');
      return false;
    }

    try {
      await deleteJob(jobId, user, handleTokenExpired);
      
      // Remove job from local state
      setJobs(prevJobs => prevJobs.filter(job => job.id !== jobId));
      
      toast.success('Job deleted', 'The job has been successfully deleted');
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete job';
      toast.error('Delete failed', errorMessage);
      return false;
    }
  };

  // Computed values
  const activeJobs = jobs.filter(job => 
    job.status === 'pending' || job.status === 'processing'
  );
  
  const completedJobs = jobs.filter(job => 
    job.status === 'completed'
  );
  
  const failedJobs = jobs.filter(job => 
    job.status === 'failed'
  );
  
  const stats = {
    total: jobs.length,
    active: activeJobs.length,
    completed: completedJobs.length,
    failed: failedJobs.length,
    pending: jobs.filter(job => job.status === 'pending').length,
    processing: jobs.filter(job => job.status === 'processing').length
  };

  return {
    jobs,
    isLoading,
    error,
    isAuthenticated,
    stats,
    activeJobs,
    completedJobs,
    failedJobs,
    loadJobs,
    refreshJobs,
    downloadJob,
    deleteJobById,
    startPollingJob,
    addJob,
    getJobById
  };
}; 